import React, { CSSProperties, useRef } from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Box,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface Column {
  field: string;
  headerName: string;
  headerCustomStyles?: CSSProperties;
  alignSelf?: 'left' | 'center' | 'right';
  cellCustomStyle?: CSSProperties;
}

interface BkTableProps {
  data: any[];
  columns: Column[];
  renderCell?: (row: any, column: Column) => React.ReactNode;
  initialRowsPerPage: number;
  footer?: string;
  renderPagination?: boolean;
  smallHeader?: boolean;
  GroupedHeader?: React.ReactNode;
  reorderable?: boolean;
  onReorder?: (descriptionInfo: { dragIndex: number; dropIndex: number; text: string; id: number }) => void;
  page?: number;
  rowsPerPage?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  paginationMode?: 'client' | 'server';
  isLoading?: boolean
}

const BkTable: React.FC<BkTableProps> = ({
  columns,
  data,
  initialRowsPerPage,
  renderCell,
  footer,
  renderPagination = true,
  smallHeader = false,
  GroupedHeader,
  reorderable,
  onReorder,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  paginationMode = 'server',
  isLoading = false,
}) => {
  const [clientPage, setClientPage] = React.useState<number>(0);
  const [clientRowsPerPage, setClientRowsPerPage] = React.useState(initialRowsPerPage);

  const handleChangePage = (event: any, newPage: number) => {
    if (paginationMode === 'client') {
      setClientPage(newPage);
    } else {
      onPageChange && onPageChange(newPage);
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (paginationMode === 'client') {
      setClientRowsPerPage(parseInt(event.target.value));
      setClientPage(0);
    } else {
      onRowsPerPageChange && onRowsPerPageChange(parseInt(event.target.value));
      onPageChange && onPageChange(0);
    }
  };

  const rowRefs = useRef<Record<number, HTMLTableRowElement | null>>({});

  const handleDragStart = (e: React.DragEvent<HTMLTableCellElement>, index: number) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('draggedIndex', index.toString());

    const dragRow = rowRefs.current[index];
    if (dragRow) {
      const clone = dragRow.cloneNode(true) as HTMLElement;

      clone.style.background = '#fff';
      clone.style.width = `${dragRow.offsetWidth / 16}rem`;
      clone.style.height = `${dragRow.offsetHeight / 16}rem`;
      clone.style.position = 'absolute';
      clone.style.opacity = '1';

      document.body.appendChild(clone);
      e.dataTransfer.setDragImage(clone, 0, 0);

      setTimeout(() => {
        document.body.removeChild(clone);
      }, 0);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLTableRowElement>, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('draggedIndex'), 10);
    if (isNaN(dragIndex) || dragIndex === dropIndex) return;

    const newData = [...data];
    const [moved] = newData.splice(dragIndex, 1);

    newData.splice(dropIndex, 0, moved);
    onReorder && onReorder({ dragIndex, dropIndex, text: moved.description, id: moved.id });
  };

  const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>) => {
    e.preventDefault();
  };

  const getVisibleRows = () => {
    if (paginationMode === 'client') {
      const start = clientPage * clientRowsPerPage;
      return data.slice(start, start + clientRowsPerPage);
    }
    return data;
  };

  return (
    <TableContainer component={Paper} sx={{ border: 'none', borderRadius: 0, boxShadow: 'none' }}>
      <Table>
        <TableHead
          sx={{
            backgroundColor: '#F7F7F8',
            borderRadius: 0,
            border: '0.0625rem solid #ECE9DC',
            boxShadow: 'none',
          }}
        >
          {GroupedHeader}
          <TableRow>
            {columns.map((row, index) => (
              <TableCell
                sx={{
                  textAlign: row.field === 'action' || row.field === 'actions' ? 'center' : 'left',
                  fontFamily: 'Instrument Sans',
                  fontSize: '0.875rem',
                  lineHeight: '1.25rem',
                  fontWeight: 600,
                  color: '#1D1B20',
                  padding: smallHeader ? '0.5rem 0.75rem' : '1.5rem 0.75rem',
                  ...row?.headerCustomStyles,
                }}
                key={index}
              >
                {row.headerName}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody sx={{ borderRadius: 0, border: '0.0625rem solid #ECE9DC' }}>
          {getVisibleRows().map((row, index) => (
            <TableRow
              ref={(el) => (rowRefs.current[index] = el)}
              onDrop={reorderable ? (e) => handleDrop(e, index) : undefined}
              onDragOver={reorderable ? handleDragOver : undefined}
              key={index}
            >
              {columns.map((item) => (
                <TableCell
                  key={item.field}
                  draggable={item.field === 'reorder'}
                  onDragStart={item.field === 'reorder' ? (e) => handleDragStart(e, index) : undefined}
                  sx={{
                    maxWidth: '15.625rem',
                    textAlign: item.alignSelf ? item.alignSelf : 'left',
                    fontFamily: 'Instrument Sans',
                    fontSize: '0.875rem',
                    lineHeight: '1.25rem',
                    fontWeight: 400,
                    padding: '0.75rem 0.75rem',
                    ...item.cellCustomStyle,
                  }}
                >
                  {renderCell ? renderCell(row, item) : row[item.field]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {renderPagination && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {footer && (
            <Box ml={2}>
              <Typography variant="body2">{footer}</Typography>
            </Box>
          )}
          <TablePagination
            rowsPerPageOptions={[5, 10, 15, 20, 50, 100]}
            labelRowsPerPage=""
            disabled={isLoading}
            component="div"
            count={paginationMode === 'client' ? data.length : (totalCount as number)}
            rowsPerPage={paginationMode === 'client' ? clientRowsPerPage : (rowsPerPage as number)}
            page={paginationMode === 'client' ? clientPage : (page as number)}
            slots={{
              actions: {
                nextButtonIcon: ArrowForwardIcon,
                previousButtonIcon: ArrowBackIcon,
              },
            }}
            slotProps={{
              select: {
                renderValue: (value) => `Show ${value}`,
                sx: {
                  fontFamily: 'Instrument Sans, Arial',
                  fontSize: '1rem',
                  lineHeight: '1.25rem',
                },
              },
            }}
            onRowsPerPageChange={handleChangeRowsPerPage}
            onPageChange={handleChangePage}
            sx={{
              width: '100%',
              '& .MuiSelect-select:focus': {
                backgroundColor: 'white',
                borderRadius: '0.3125rem',
              },
              '.MuiTablePagination-select': {
                backgroundColor: 'white',
                borderRadius: '0.3125rem',
                width: '6.25rem',
                border: '0.0625rem solid #7e828b',
                display: 'flex',
                justifyContent: 'start',
                alignItems: 'center',
                height: '1.75rem !important',
              },
              '.MuiTablePagination-spacer	': {
                display: 'none',
              },
              '.MuiTablePagination-toolbar': {
                display: 'flex',
                paddingRight: '1.25rem',
                paddingLeft: '0rem',
              },
              '.MuiTablePagination-displayedRows': {
                marginLeft: 'auto',
                fontFamily: 'Instrument Sans, Arial',
                fontSize: '1rem',
                lineHeight: '1.25rem',
              },
            }}
          />
        </Box>
      )}
    </TableContainer>
  );
};

export default BkTable;
